import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Clock, Home, User, Wallet } from 'lucide-react';
import StandardLayout from '../components/common/StandardLayout';
import PageHeader from '../components/common/PageHeader';
import LoadingSpinner from '../components/common/LoadingSpinner';
import SetCapitalDialog from '../components/broker/SetCapitalDialog';
import SetCheckStartDialog from '../components/broker/SetCheckStartDialog';
import ConnectTelegramCard from '../components/telegram/ConnectTelegramCard';
import Button from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import brokerService from '../services/brokerService';
import { useAuth } from '../context/AuthContext';
import { formatCheckStart } from '../utils/checkStart';
import type { BrokerAccount } from '../types';

/**
 * Minimal Account page — the user-menu "Account" destination. White-label
 * users don't see the full Brokers admin surface, so this page is their
 * self-service home for the one configurable field they need: trading
 * capital per broker account.
 *
 * Quantleap users see the same page — it's a lightweight subset of the
 * Brokers page focused on the most common edit.
 */
const AccountPage: React.FC = () => {
  const { user } = useAuth();
  const [accounts, setAccounts] = useState<BrokerAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkStartDialogOpen, setCheckStartDialogOpen] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const accs = await brokerService.getBrokerAccounts();
      setAccounts(accs);
    } catch (e: any) {
      setError(e?.message || 'Failed to load broker accounts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const breadcrumbs = useMemo(() => [
    { label: 'Home', href: '/', icon: <Home className="w-3.5 h-3.5" /> },
    { label: 'Account' },
  ], []);

  const hasMissingCapital = accounts.some((a) => !a.capital || a.capital <= 0);

  return (
    <StandardLayout>
      <PageHeader
        title="Account"
        pageType="profile"
        icon={<User className="w-5 h-5" />}
        breadcrumbs={breadcrumbs}
      />

      {/* User info card */}
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Name
              </div>
              <div className="text-gray-900 dark:text-white mt-1">
                {user?.fullName || '—'}
              </div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Email
              </div>
              <div className="text-gray-900 dark:text-white mt-1 break-all">
                {user?.email || '—'}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Trading capital card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Trading capital</CardTitle>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setDialogOpen(true)}
              disabled={accounts.length === 0}
              leftIcon={<Wallet className="w-4 h-4" />}
            >
              Edit capital
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 flex justify-center">
              <LoadingSpinner message="Loading accounts..." />
            </div>
          ) : error ? (
            <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No broker accounts found. Capital can be set once an account is linked.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                Capital is the denominator for Total Return %, CAGR and Sharpe ratio
                on the Analytics page. Set the ₹ amount you've allocated for
                algo trading on each account.
              </p>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {accounts.map((a) => {
                  const missing = !a.capital || a.capital <= 0;
                  return (
                    <div key={a.client_id} className="py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                          {a.broker_name}
                          <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">{a.client_id}</span>
                        </div>
                        {missing && (
                          <div className="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
                            Capital not set — percent metrics on Analytics will show "—"
                          </div>
                        )}
                      </div>
                      <div className="shrink-0 text-right">
                        <div className={`text-sm font-semibold tabular-nums ${missing ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>
                          ₹{(a.capital ?? 0).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {hasMissingCapital && (
                <div className="mt-4 text-xs text-gray-500 dark:text-gray-400">
                  Click <strong>Edit capital</strong> to set capital on accounts above.
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Checks start time card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle>Checks start time</CardTitle>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setCheckStartDialogOpen(true)}
              disabled={accounts.length === 0}
              leftIcon={<Clock className="w-4 h-4" />}
            >
              Edit start time
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-6 flex justify-center">
              <LoadingSpinner message="Loading accounts..." />
            </div>
          ) : error ? (
            <div className="text-sm text-red-700 dark:text-red-300">{error}</div>
          ) : accounts.length === 0 ? (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              No broker accounts found.
            </p>
          ) : (
            <>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                The time each account's per-minute checks begin — both management
                (stop-loss, target, trailing, MTM, order sync) and the monitored
                entries (Wait &amp; Trade, ORB, re-entry). Before it, those entries
                don't fire and existing positions aren't managed. Scheduled
                entries/exits are unaffected. Default 09:16.
              </p>
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {accounts.map((a) => (
                  <div key={a.client_id} className="py-3 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-white capitalize">
                        {a.broker_name}
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">{a.client_id}</span>
                      </div>
                    </div>
                    <div className="shrink-0 text-sm font-semibold tabular-nums text-gray-900 dark:text-white">
                      {formatCheckStart(a.check_start_hour, a.check_start_minute)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Telegram notifications card */}
      <ConnectTelegramCard />

      <SetCapitalDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        accounts={accounts}
        onSaved={fetchAccounts}
      />

      <SetCheckStartDialog
        isOpen={checkStartDialogOpen}
        onClose={() => setCheckStartDialogOpen(false)}
        accounts={accounts}
        onSaved={fetchAccounts}
      />
    </StandardLayout>
  );
};

export default AccountPage;
